import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useVerifyMfa } from "../../hooks/useVerifyMfa";

interface MfaChallengeModalProps {
  open: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

export const MfaChallengeModal: React.FC<MfaChallengeModalProps> = ({
  open,
  onSuccess,
  onCancel,
}) => {
  const [code, setCode] = useState("");
  const { mutate: verifyMfa, isPending, error, reset } = useVerifyMfa();

  const handleSubmit = () => {
    verifyMfa({ code: code.replace(/\s/g, "") }, { onSuccess });
  };

  const handleClose = () => {
    setCode("");
    reset();
    onCancel();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Two-Factor Authentication</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter the 6-digit code from your authenticator app, or one of your
          backup codes.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Invalid code. Please try again.
          </Alert>
        )}
        <TextField
          autoFocus
          fullWidth
          label="Authentication Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          inputProps={{
            maxLength: 10,
            inputMode: "numeric",
            pattern: "[0-9A-Fa-f]*",
          }}
          onKeyDown={(e) =>
            e.key === "Enter" && code.length >= 6 && handleSubmit()
          }
          disabled={isPending}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={code.length < 6 || isPending}
          startIcon={isPending ? <CircularProgress size={16} /> : undefined}
        >
          {isPending ? "Verifying…" : "Verify"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
